import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  List,
  ListItem,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BrregOrgDto } from "../../../autogen/bff-api";
import { useBaseData } from "../../../common/useBaseData";
import { useBrregAutoFill } from "../signup/useBrregAutoFill";
import { useBrregNameSearch } from "../signup/useBrregNameSearch";

export interface OrgInfoFieldValues {
  companyName: string;
  countryId: string;
  organizationNumber: string;
}

export const OrgInfoFields = () => {
  const { t } = useTranslation();
  const baseData = useBaseData();
  const { register, setValue, watch, getValues, formState } = useFormContext<OrgInfoFieldValues>();
  const errors = formState.errors;

  const norwayCountryId = baseData.countries.find((c) => c.alpha3Code === "NOR")?.id;
  const companyName = watch("companyName") ?? "";
  const countryId = watch("countryId");
  const organizationNumber = watch("organizationNumber");
  const isNorwaySelected = !!norwayCountryId && countryId === norwayCountryId;

  const [isNameFocused, setIsNameFocused] = useState(false);
  const lastSelectedNameRef = useRef<string | null>(null);
  const isAfterSelection =
    lastSelectedNameRef.current !== null && companyName === lastSelectedNameRef.current;

  const { matches, isFetching: isSearching } = useBrregNameSearch({
    countryId,
    query: companyName,
    enabled: isNameFocused && !isAfterSelection,
  });

  const { brregInfo } = useBrregAutoFill({ countryId, organizationNumber });

  useEffect(() => {
    if (brregInfo && !getValues("companyName")) {
      setValue("companyName", brregInfo.name);
    }
  }, [brregInfo, getValues, setValue]);

  const handleSelectBrregOrg = (org: BrregOrgDto) => {
    lastSelectedNameRef.current = org.name;
    setValue("companyName", org.name, { shouldValidate: true });
    setValue("organizationNumber", org.organizationNumber, { shouldValidate: true });
    setIsNameFocused(false);
  };

  const showDropdown =
    isNameFocused && !isAfterSelection && isNorwaySelected && (isSearching || matches.length > 0);

  const companyNameField = register("companyName", {
    required: t("Please provide the name of your company.") ?? "",
    minLength: { value: 2, message: t("Must be at least 2 characters long.") },
    onChange: () => {
      lastSelectedNameRef.current = null;
    },
  });

  return (
    <>
      <FormControl mt="5px" isInvalid={errors.companyName !== undefined}>
        <FormLabel htmlFor="companyName">{t("Company name")}</FormLabel>
        <Box position="relative">
          <Input
            id="companyName"
            type="text"
            autoComplete="off"
            {...companyNameField}
            onFocus={() => setIsNameFocused(true)}
            onBlur={(e) => {
              companyNameField.onBlur(e);
              window.setTimeout(() => setIsNameFocused(false), 150);
            }}
          />
          {showDropdown && (
            <Box
              position="absolute"
              top="full"
              left={0}
              right={0}
              mt={1}
              bg="smBackground"
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="md"
              boxShadow="md"
              zIndex={10}
              maxH="280px"
              overflowY="auto"
            >
              {isSearching && matches.length === 0 && (
                <Flex p={3} align="center" gap={2}>
                  <Spinner size="xs" />
                  <Text fontSize="sm" color="smMuted">
                    {t("Searching Brønnøysund")}…
                  </Text>
                </Flex>
              )}
              <List>
                {matches.map((org) => (
                  <ListItem
                    key={org.organizationNumber}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "smBackgroundTertiary" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectBrregOrg(org);
                    }}
                  >
                    <Text fontWeight="semibold" fontSize="sm">
                      {org.name}
                    </Text>
                    <Text fontSize="xs" color="smMuted">
                      {org.organizationNumber}
                    </Text>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
        <FormErrorMessage>{errors.companyName?.message}</FormErrorMessage>
      </FormControl>

      <FormControl id="country" mt="5px" isInvalid={errors.countryId != null}>
        <FormLabel>{t("Company location")}</FormLabel>
        <Select
          placeholder={t("Select a country for your company") ?? ""}
          {...register("countryId", {
            required: { value: true, message: t("Select a country for your company.") },
          })}
        >
          {baseData.countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <FormErrorMessage>{errors.countryId?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mt="5px" isInvalid={errors.organizationNumber !== undefined}>
        <FormLabel htmlFor="organizationNumber">{t("Organization number")}</FormLabel>
        <Input
          id="organizationNumber"
          type="text"
          {...register("organizationNumber", {
            required: t("Please provide an organization number.") ?? "",
            minLength: isNorwaySelected
              ? { value: 9, message: t("Must be at least 9 digits.") }
              : { value: 2, message: t("Must be at least 2 digits.") },
            maxLength: isNorwaySelected ? { value: 9, message: t("Must be at most 9 digits.") } : undefined,
          })}
        />
        <FormErrorMessage>{errors.organizationNumber?.message}</FormErrorMessage>
      </FormControl>
    </>
  );
};
